import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { Alert, Button, Tooltip } from '@mui/material';
// hooks
import useAuth from '../hooks/useAuth';
import useLocales from '../hooks/useLocales';
import usePermissions from '../hooks/usePermissions';
// redux
import { useSelector } from '../redux/store';
// routes
import { PATH_DASHBOARD } from '../routes/paths';
// utils
import { resolvePlanTier, getVenueLimit, TIER_LABELS } from '../utils/planGating';
// translations
import { tokens } from '../locales/tokens';

// ----------------------------------------------------------------------

const BILLING_PATH = PATH_DASHBOARD.organization.subscription;

type Props = {
  /** `compact` shows a one-line message (full text moves to a tooltip) —
   * for tight spaces like the popovers */
  compact?: boolean;
  /** Whole alert becomes the "Gerenciar assinatura" link instead of showing
   * a separate action button — for spots where every extra control competes
   * for room, like the account popover */
  asLink?: boolean;
  /** Called when the alert is clicked/navigated — e.g. to close a parent popover */
  onNavigate?: () => void;
  sx?: object;
};

/**
 * Warns when the org has more active venues than the plan allows.
 * Mounted once in DashboardLayout so it stays visible on every page.
 * Compact/asLink variants remain available for popovers.
 * Hidden when the effective profile cannot manage billing (logged-in user
 * or preview). Compact/asLink variants remain available for popovers.
 */
export default function VenueLimitAlert({ compact = false, asLink = false, onNavigate, sx }: Props) {
  const { t } = useLocales();
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { venues } = useSelector((state) => state.venue);

  if (!can('billing.manage')) {
    return null;
  }

  const planTier = resolvePlanTier(user?.plan_tier);
  const venueLimit = getVenueLimit(planTier);
  const activeVenuesCount = venues.filter((v) => v.status).length;
  const overLimit = venueLimit !== null && activeVenuesCount > venueLimit;

  if (!overLimit) return null;

  const fullMessage = t(tokens.pages.venues.overLimitBanner, {
    count: activeVenuesCount,
    limit: venueLimit,
    tier: TIER_LABELS[planTier],
  });

  const handleClick = () => {
    navigate(BILLING_PATH);
    onNavigate?.();
  };

  const messageNode = compact ? (
    <Tooltip title={fullMessage} placement="top" arrow>
      <span>{t(tokens.pages.venues.overLimitShort)}</span>
    </Tooltip>
  ) : (
    fullMessage
  );

  if (asLink) {
    return (
      <Alert
        severity="warning"
        onClick={handleClick}
        sx={{
          mb: compact ? 0 : 3,
          cursor: 'pointer',
          '&:hover': { opacity: 0.85 },
          ...sx,
        }}
      >
        {messageNode}
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      sx={{ mb: compact ? 0 : 2, mx: compact ? undefined : { xs: 2, lg: 0 }, ...sx }}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to={BILLING_PATH}
          onClick={onNavigate}
        >
          {t(tokens.pages.venues.overLimitCta)}
        </Button>
      }
    >
      {messageNode}
    </Alert>
  );
}
